import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";

const empty = {
  slug: "",
  title: "",
  category: "Algemeen",
  excerpt: "",
  read_time: 5,
  thumbnail: "",
  content: "",
  published: true,
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data || []));
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!editing.slug || !editing.title) {
      toast.error("Slug en titel zijn verplicht");
      return;
    }
    await api.post("/admin/blog", editing);
    toast.success("Opgeslagen");
    setEditing(null);
    load();
  };
  const remove = async (slug) => {
    if (!confirm("Verwijderen?")) return;
    await api.delete(`/admin/blog/${slug}`);
    toast.success("Verwijderd");
    load();
  };

  return (
    <div className="space-y-7">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-oc-primary text-[34px]">Blog</h1>
          <p className="text-oc-textSecondary mt-1">{posts.length} artikels</p>
        </div>
        <Button onClick={() => setEditing({ ...empty })} className="bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn">
          <Plus className="w-4 h-4 mr-2" /> Nieuw artikel
        </Button>
      </div>

      {editing && (
        <div className="bg-white border border-oc-border rounded-oc p-7 space-y-4">
          <h2 className="font-display font-semibold text-oc-primary text-[20px]">
            {posts.some((p) => p.slug === editing.slug) ? "Bewerken" : "Nieuw artikel"}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Slug</Label>
              <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="h-12 rounded-oc-btn" />
            </div>
            <div className="space-y-2">
              <Label>Categorie</Label>
              <Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="h-12 rounded-oc-btn" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Titel</Label>
            <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="h-12 rounded-oc-btn" />
          </div>
          <div className="space-y-2">
            <Label>Excerpt</Label>
            <Textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="rounded-oc-btn" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Leestijd (min)</Label>
              <Input type="number" value={editing.read_time} onChange={(e) => setEditing({ ...editing, read_time: Number(e.target.value) || 5 })} className="h-12 rounded-oc-btn" />
            </div>
            <div className="space-y-2">
              <Label>Thumbnail URL</Label>
              <Input value={editing.thumbnail} onChange={(e) => setEditing({ ...editing, thumbnail: e.target.value })} className="h-12 rounded-oc-btn" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Inhoud</Label>
            <RichTextEditor
              value={editing.content}
              onChange={(html) => setEditing({ ...editing, content: html })}
              minHeight="320px"
              placeholder="Schrijf hier het blog-artikel..."
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Switch checked={editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
              <span className="text-[14px] text-oc-textPrimary">Gepubliceerd</span>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)}>Annuleren</Button>
              <Button onClick={save} className="bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn">
                <Save className="w-4 h-4 mr-2" /> Opslaan
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-oc-border rounded-oc divide-y divide-oc-border">
        {posts.map((p) => (
          <div key={p.slug} className="px-6 py-4 flex items-center justify-between gap-4">
            <div>
              <p className="font-display font-semibold text-oc-primary">{p.title}</p>
              <p className="text-oc-textSecondary text-[13px]">/{p.slug} · {p.category}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] uppercase font-semibold px-2 py-0.5 rounded-md ${p.published ? "oc-status-fair" : "bg-oc-subtle text-oc-textSecondary"}`}>
                {p.published ? "Live" : "Verborgen"}
              </span>
              <Button variant="ghost" size="sm" onClick={() => setEditing({ ...p })}>Bewerken</Button>
              <Button variant="ghost" size="sm" className="text-oc-excessive hover:bg-oc-excessive/10" onClick={() => remove(p.slug)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
